import type { ReactNode } from 'react';
import { createContext, useContext, useMemo, useState } from 'react';

import { scrollToTop } from '@/components/RetailerWidget/components/RetailerContactUs/helpers';
import type { StrapHit, WatchHit } from '@/integrations/algolia/types';

interface RetailerPageContextType {
  handleDiscoverCollection: () => void;
  retailerPage: 'selection' | 'new';
  selectedProduct: WatchHit | StrapHit | null;
  setRetailerPage: (value: 'selection' | 'new') => void;
  setSelectedProduct: (product: WatchHit | StrapHit | null) => void;
}

export const RetailerPageContext = createContext<
  RetailerPageContextType | undefined
>(undefined);

export const useRetailerPageContext = () => {
  const context = useContext(RetailerPageContext);
  if (!context) {
    throw new Error(
      'useRetailerPageContext must be used within a RetailerPageProvider',
    );
  }
  return context;
};

export const RetailerPageProvider = ({
  children,
  initialPage = 'selection',
}: {
  children: ReactNode;
  initialPage?: 'selection' | 'new';
}) => {
  const [retailerPage, setRetailerPage] = useState<'selection' | 'new'>(
    initialPage,
  );
  const [selectedProduct, setSelectedProduct] = useState<
    WatchHit | StrapHit | null
  >(null);

  const handleDiscoverCollection = () => {
    Promise.all([setSelectedProduct(null), setRetailerPage('selection')]).then(
      () => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            scrollToTop();
          });
        });
      },
    );
  };
  const value = useMemo(
    () => ({
      retailerPage,
      setRetailerPage,
      selectedProduct,
      setSelectedProduct,
      handleDiscoverCollection,
    }),
    [retailerPage, selectedProduct, handleDiscoverCollection],
  );

  return (
    <RetailerPageContext.Provider value={value}>
      {children}
    </RetailerPageContext.Provider>
  );
};
